import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Weak identity ownership: no disk persistence, no reuse across logins or
// refreshed user/grant objects. Keep only a few recently viewed date ranges.
const sessions = new WeakMap();
const TTL = 30_000;
const EMPTY = [];
const cached = (store, key) => {
  const entry = store?.get(key);
  return entry && Date.now() - entry.at < TTL ? entry.rows : null;
};

export const useExpenseList = ({ ledger, dateFrom, dateTo }) => {
  const { user } = useAuth();
  const management = ledger === "mgmt_usa_nj";
  const query = new URLSearchParams({ ledger, date_from: dateFrom || "", date_to: dateTo || "" });
  if (management) query.set("compact", "true");
  const url = `/expenses?${query}`;
  if (management && user && !sessions.has(user)) sessions.set(user, new Map());
  const store = management && user ? sessions.get(user) : null;
  const [state, setState] = useState(() => ({ user, url, rows: cached(store, url), error: "", loading: true }));
  const request = useRef(null);
  const sequence = useRef(0);
  const load = useCallback(async (refresh = true) => {
    request.current?.abort();
    const controller = new AbortController(); request.current = controller;
    const n = ++sequence.current;
    if (refresh) store?.clear();
    setState(previous => ({ user, url, rows: previous.user === user && previous.url === url ? previous.rows : cached(store, url), loading: true, error: "" }));
    try {
      const { data } = await api.get(url, { signal: controller.signal });
      if (n !== sequence.current || controller.signal.aborted) return;
      if (store) {
        store.set(url, { at: Date.now(), rows: data });
        if (store.size > 5) store.delete(store.keys().next().value);
      }
      setState({ user, url, rows: data, loading: false, error: "" });
    } catch (error) {
      if (controller.signal.aborted || n !== sequence.current) return;
      const denied = [401, 403].includes(error.response?.status);
      if (denied) store?.clear();
      setState(previous => ({ user, url, rows: denied ? null : previous.rows, loading: false,
        error: denied ? "You no longer have access to these expenses." : "Could not refresh expenses. Please try again." }));
    }
  }, [url, store, user]);
  useEffect(() => {
    load(false);
    return () => { sequence.current += 1; request.current?.abort(); };
  }, [load]);
  const current = state.user === user && state.url === url ? state : { rows: cached(store, url), loading: true, error: "" };
  return { list: current.rows || EMPTY, loading: current.loading, hasData: current.rows !== null,
    error: current.error, load };
};